import { useState } from "react";
import { useUpdateBasicData } from "src/apps/hooks/useUpdateBasicData";
import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import {
  COMPANY_NIF_ERROR,
  ID_NUMBER_ERROR,
  ERROR_MAP,
} from "../RepresentedEntityDataForm.constants";
import { buildActorBasicData } from "../helpers/buildActorBasicData";
import {
  validateCompanyNif,
  validateRepresentativeIdNumber,
} from "../helpers/representedEntityFormValidators";
import { RepresentedEntityFormData } from "./useRepresentedEntityFormData";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";

export const useRepresentedEntityForm = (
  documentationArea: DocumentationArea,
  formData: RepresentedEntityFormData,
  selectedActorTemplate?: {
    id: string;
    key: string;
    hasBeenSelectedByTheUser: boolean;
  },
) => {
  const { mutate: updateBasicData, isPending } = useUpdateBasicData();
  const { sendEvent } = useSendAnalyticEvent();

  const [companyNif, setCompanyNif] = useState<string>(
    formData.initialRepresentedEntityIdNumber.value,
  );
  const [companyName, setCompanyName] = useState<string>(formData.initialCompanyName);
  const [idNumber, setIdNumber] = useState<string>(formData.initialRepresentativeIdNumber.value);
  const [representativeRelationType, setRepresentativeRelationType] = useState<string>(
    formData.initialRepresentativeRelationType,
  );
  const [companyNifError, setCompanyNifError] = useState<string>("");
  const [idNumberError, setIdNumberError] = useState<string>("");
  const [representativeRelationTypeError, setRepresentativeRelationTypeError] =
    useState<string>("");

  const buttonDisabled =
    !companyNif ||
    !companyName ||
    !idNumber ||
    !representativeRelationType ||
    !!representativeRelationTypeError;

  const validateForm = () => {
    const isIdNumberValid = validateRepresentativeIdNumber(
      idNumber,
      formData.initialRepresentativeIdNumber.type,
      formData.initialRepresentativeIdNumber.country,
    );
    if (!isIdNumberValid) setIdNumberError(ID_NUMBER_ERROR);

    const isCompanyNifValid = validateCompanyNif(
      companyNif,
      formData.initialRepresentedEntityIdNumber.type,
      formData.initialRepresentedEntityIdNumber.country,
    );
    if (!isCompanyNifValid) setCompanyNifError(COMPANY_NIF_ERROR);

    return (
      isIdNumberValid &&
      isCompanyNifValid &&
      !!companyName &&
      !!idNumber &&
      !!representativeRelationType
    );
  };

  const onSubmit = () => {
    if (!validateForm()) return;
    if (!documentationArea) throw new DocumentationAreaNotFoundError();

    const body = buildActorBasicData({
      representative: formData.representative,
      companyName,
      idNumber,
      idNumberType: formData.initialRepresentativeIdNumber.type,
      idNumberCountry: formData.initialRepresentativeIdNumber.country,
      representedEntityIdNumber: companyNif,
      representedEntityIdNumberCountry: formData.initialRepresentedEntityIdNumber.country,
      representedEntityIdNumberType: formData.initialRepresentedEntityIdNumber.type,
      representedEntityRelationType: representativeRelationType,
    });

    body.selectedActorTemplateId = selectedActorTemplate?.id;

    updateBasicData(
      {
        actorId: documentationArea.getActor().getId(),
        basicData: body,
      },
      {
        onSuccess: () => {
          if (!selectedActorTemplate) {
            return;
          }

          sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_ACTOR_UPDATED_FROM_TEMPLATE, {
            actor_template_id: selectedActorTemplate.id,
            actor_template_key: selectedActorTemplate.key,
            has_it_been_selected_by_the_user: selectedActorTemplate.hasBeenSelectedByTheUser,
          });
        },
        onError: (error) => {
          if (!(error instanceof HTTPError)) {
            return;
          }

          const errorCode = error.getCode();
          const mappedErrorKey = errorCode ? ERROR_MAP[errorCode] : undefined;

          if (mappedErrorKey) {
            setRepresentativeRelationTypeError(mappedErrorKey);
          }
        },
      },
    );
  };

  const handleCompanyNifChange = (value: string) => {
    setCompanyNifError("");
    setCompanyNif(value);
  };

  const handleIdNumberChange = (value: string) => {
    setIdNumberError("");
    setIdNumber(value);
  };

  const handleRepresentativeRelationTypeChange = (value: string) => {
    setRepresentativeRelationTypeError("");
    setRepresentativeRelationType(value);
  };

  return {
    values: {
      companyNif,
      companyName,
      idNumber,
      representativeRelationType,
    },
    errors: {
      companyNifError,
      idNumberError,
      representativeRelationTypeError,
    },
    setters: {
      setCompanyNif: handleCompanyNifChange,
      setCompanyName,
      setIdNumber: handleIdNumberChange,
      setRepresentativeRelationType: handleRepresentativeRelationTypeChange,
    },
    buttonDisabled,
    isPending,
    onSubmit,
  };
};

export type RepresentedEntityFormState = ReturnType<typeof useRepresentedEntityForm>;
